import {layer} from '@wandb/components';
import {type RefObject, useLayoutEffect, useMemo, useState} from 'react';

import {type PlotMargin} from '../../util/plotHelpers/plotHelpers';
import {useViewportSize} from '../../util/useViewportSize';

export const FLAG_LINE_OFFSET = 12;
export const VIEWPORT_EDGE_GAP = 12;

// The hovered flag is portalled to document.body, so its z-index competes in
// the root stacking context rather than inside the panel's. It has to clear
// both the Semantic panel editor modal and the MUI workspace drawer, which sit
// on the overlay layer; the flag is a tooltip.
export const FLAG_Z_INDEX = layer('tooltip');

export type FlagPlacement = {
  left: number;
  top: number;
  maxWidth: number;
  placeLeftOfLine: boolean;
};

export function useHoveredFlagPlacement({
  wrapperRef,
  isHovered,
  plotMargin,
  crosshairLineX,
}: {
  wrapperRef: RefObject<HTMLElement | null>;
  isHovered: boolean;
  plotMargin: PlotMargin;
  crosshairLineX: number | null;
}): FlagPlacement {
  const {width: viewportWidth} = useViewportSize();
  const [wrapperRect, setWrapperRect] = useState<DOMRect | undefined>(
    undefined
  );

  useLayoutEffect(() => {
    if (!isHovered) {
      setWrapperRect(undefined);
      return;
    }
    const measure = () =>
      setWrapperRect(wrapperRef.current?.getBoundingClientRect());
    measure();
    // capture:true so scrolls in any ancestor scroll container are caught.
    window.addEventListener('scroll', measure, {passive: true, capture: true});
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, {capture: true});
      window.removeEventListener('resize', measure);
    };
  }, [isHovered, wrapperRef]);

  return useMemo(() => {
    const left =
      (wrapperRect?.left ?? 0) + (plotMargin.left ?? 0) + (crosshairLineX ?? 0);
    const top = (wrapperRect?.top ?? 0) + (plotMargin.top ?? 0);

    const spaceRightOfLine = viewportWidth - left;
    const spaceLeftOfLine = left;
    const placeLeftOfLine = spaceLeftOfLine > spaceRightOfLine;

    const maxWidth = Math.max(
      0,
      (placeLeftOfLine
        ? left - FLAG_LINE_OFFSET
        : viewportWidth - left - FLAG_LINE_OFFSET) - VIEWPORT_EDGE_GAP
    );

    return {left, top, maxWidth, placeLeftOfLine};
  }, [
    wrapperRect,
    viewportWidth,
    plotMargin.left,
    plotMargin.top,
    crosshairLineX,
  ]);
}
